import { Link, useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/UseToast";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { customerLogin, customerOtp, User } from "@/features/auth/AuthSlice";
import { IMAGES } from "@/assets/IMAGES";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/InputOtp";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { OTPFormValues, otpSchema } from "@/schema/LoginSchema";
import CommonButton from "@/components/custom-component/CommonButton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { fetchToken } from "@/utils/lib/Firebase";
import { maskEmailInText } from "@/utils/Helper";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Button } from "@/components/ui/Button";

const initailValue = {
  email: "",
  rememberme: false,
};

const { VITE_APP_SITEKEY, VITE_ORG_ID, 
  
  VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } =
  import.meta.env;

export default function UserLoginOtp() {
  useDocumentTitle("Verify your OTP");
  const location = useLocation();
  const userPersona = localStorage.getItem("userType");
  // const { userInfo } = useSelector((state: RootState) => state.auth);
  // const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const [userInfo, setUserInfo] = useState<User>(); // Initialize userInfo as an empty object
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false); // Add state for verifying OTP

  useEffect(() => {
    const userInfoString = localStorage.getItem("userInfo");
    if (userInfoString) {
      const parsedUserInfo = JSON.parse(userInfoString);
      setUserInfo(parsedUserInfo); // Update state with parsed userInfo
    }
    setIsLoading(false); // Mark loading as complete
  }, []);

  useEffect(() => {
    if (
      isAuthenticated &&
      userPersona === "RemoteUser" &&
      location.pathname === "/user-login-otp"
    ) {
      navigate("/user/home");
    }
  }, [location]);

  const { toast } = useToast();
  const navigate = useNavigate(); // Initialize the navigation hook
  const dispatch = useDispatch<AppDispatch>();
  const { email, emailResponse } = location.state || {};

  const { loading, error, isEmailVerify } = useSelector(
    (state: RootState) => state.auth
  );

  const isAuthenticated = Boolean(localStorage.getItem("userToken"));

  const handleClick = () => {
    navigate("/"); // Navigate to the root path when clicked
  };

  const [remainingTime, setRemainingTime] = useState<number>(
    emailResponse?.otp_expiry_seconds || 0
  );

  // Format the remaining time to MM:SS format
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
    reset,
  } = useForm<OTPFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: "" },
  });

  const onSubmit = async (data: OTPFormValues) => {
    setIsVerifying(true);
    const fcm_token: string = (await fetchToken()) ?? "";
    localStorage.setItem("fcm_token", fcm_token);
    if (data.otp) {
      dispatch(
        customerOtp({
          email,
          otp: data.otp,
          fcm_token: fcm_token,
          its_id: emailResponse.its_id,
          domain:
            VITE_IS_LOCAL === "true"
              ? VITE_LOCAL_URL
              : VITE_IS_PRODUCTION === "true"
                ? window.location.hostname
                : window.location.host,
        })
      )
        .unwrap()
        .then((response) => {
          toast({
            variant: "success",
            description: (
              <div className="flex items-center space-x-2 w-full h-10">
                <img
                  src={IMAGES.Status_icon}
                  alt="Success"
                  className="w-6 h-6"
                />
                <span>{response.message}</span>
              </div>
            ),
          });
          if (!userInfo?.first_name?.trim()) {
            navigate("/user/register-user"); // Navigate to register if first_name is invalid
          } else {
            navigate("/user/home"); // Navigate to user dashboard if first_name is valid
          }
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            description: error,
          });
        })
        .finally(() => {
          setIsVerifying(false);
        });
    } else {
      setIsVerifying(false); // Set verifying state to false
    }
  };

  const onResentOtp = async () => {
    const email = emailResponse.its_id;
    dispatch(
      customerLogin({
        identifier: email,
        org_id: VITE_ORG_ID,
        rememberme: false,
        domain:
          VITE_IS_LOCAL === "true"
            ? VITE_LOCAL_URL
            : VITE_IS_PRODUCTION === "true"
              ? window.location.hostname
              : window.location.host,
      })
    )
      .unwrap()
      .then(({ message, ...response }) => {
        toast({
          variant: "success",
          description: (
            <div className="flex items-center space-x-2 w-full h-10">
              <img src={IMAGES.Status_icon} alt="Success" className="w-6 h-6" />
              <span>{message}</span>
            </div>
          ),
        });
        setRemainingTime(response?.otp_expiry_seconds);
        reset({ otp: "" }); // Reset the OTP field
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          description: error,
        });
      });
  };

  // Watch the OTP input and trigger submit when fully filled
  // Watch OTP field
  const otp = watch("otp");
  useEffect(() => {
    if (otp && otp.length === 6) {
      trigger().then((isValid) => {
        if (isValid) {
          handleSubmit(onSubmit)();
        }
      });
    }
  }, [otp, trigger, handleSubmit]);

  useEffect(() => {
    // Start countdown timer when otp_expiry_seconds is available
    if (remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);

      // Clear interval when the component is unmounted or remainingTime reaches 0
      return () => clearInterval(timer);
    }
  }, [remainingTime]);

  return (
    <div className="loginpage-image">
      <div className="max-w-[1800px] mx-auto xs:mx-0">
        <div className="flex sm:mx-16 justify-center sm:justify-between items-center h-dvh xs:mx-0  ">
          <div className=" flex flex-col gap-2 xs:gap-0"></div>
          <div className="px-2 xs:pt-[100px] md:pt-0 md:pb-10">
            <Card
              // className=" max-w-md bg-transparent bg-opacity-10
              // backdrop-blur-sm custom-gradient-bg rounded-3xl border-checkboxborder shadow-lg
              //  "
              className="px-16 max-w-md bg-transparent bg-opacity-10 backdrop-blur-sm custom-gradient-bg rounded-3xl border-checkboxborder shadow-lg p-2"
            >
              {/* <CardHeader>
                <CardTitle>
                  <div className="gap-1 flex justify-start items-center">
                    <div className="flex flex-col gap-2">
                      <h1 className="font-bold  tracking-wider leading-5 text-[1.3rem] text-primary">
                        Enter
                      </h1>
                      <p className="text-textwhite leading-5  font-semibold text-[1.8rem]">
                        OTP Code
                      </p>
                      <p className="text-textgray leading-5 text-[0.9rem]">
                        {maskEmailInText(emailResponse.message)}
                      </p>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader> */}
              <CardHeader>
                <CardTitle>
                  <div className="gap-1 flex justify-center items-center">
                    <div>
                      <img
                        src={IMAGES.logo_icon}
                        height={110}
                        width={100}
                        alt="Main logo"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-primary leading-5 italic font-semibold text-[1.8rem]">
                        Enter Code
                      </p>
                      <p className="text-textgray leading-5 text-[0.9rem]">
                        We sent OTP code to your email address
                      </p>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="w-full">
                <form
                  className="space-y-4 w-full"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex flex-col items-center w-full">
                    <Controller
                      name="otp"
                      control={control}
                      render={({ field }) => (
                        <InputOTP
                          maxLength={6}
                          {...field}
                          className="w-full p-2"
                          disabled={isVerifying}
                        >
                          <InputOTPGroup>
                            {[...Array(6)].map((_, index) => {
                              const uniqueKey = `otp-slot-${index}`;
                              return (
                                <InputOTPSlot key={uniqueKey} index={index} />
                              );
                            })}
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {errors.otp && (
                      <p className="text-red-500 text-sm mt-2 w-full px-14">
                        {errors.otp.message}
                      </p>
                    )}

                    <div className="flex justify-between items-center w-full mt-3 ">
                      <p className="text-[.8rem] text-primary">
                        Remaining time:{" "}
                        <span className="text-textgray">
                          {formatTime(remainingTime)}
                        </span>
                      </p>
                      <TooltipProvider>
                        {remainingTime < 90 ? (
                          <button
                            onClick={onResentOtp}
                            disabled={remainingTime >= 90}
                            className="disabled:cursor-not-allowed text-[.8rem] text-primary disabled:text-gray-400 underline hover:cursor-pointer"
                          >
                            Resend OTP
                          </button>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger
                              asChild
                              className="hover:cursor-pointer"
                            >
                              <button
                                onClick={onResentOtp}
                                disabled={remainingTime >= 90}
                                className="disabled:cursor-not-allowed text-[.8rem] text-primary disabled:text-gray-400 underline hover:cursor-pointer"
                              >
                                Resend OTP
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-52">
                              You can request a new OTP 30 seconds after the
                              last OTP request.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isVerifying}
                    className="w-full text-black  hover:text-white hover:bg-primary"
                  >
                    {isVerifying ? "Verifying OTP" : "Verify OTP & Continue"}
                    {isVerifying && (
                      <div className="w-5 h-5 ml-2 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                    )}
                  </Button>
                </form>
                <div className="flex justify-center items-center"></div>

                {/* <div className="flex justify-center items-center mt-6">
                  <h3 className="text-white font-normal  text-sm mx-2 text-center">
                    By using this app you agree to the Privacy statements
                    <p className="Poppins text-textgray text-sm">
                      <Link
                        className="text-primary underline"
                        to="/privacy-policy"
                      >
                        Privacy Statement
                      </Link>{" "}
                      -{" "}
                      <Link
                        className="text-primary underline"
                        to="/terms-and-conditions"
                      >
                        Terms & condition{" "}
                      </Link>
                    </p>
                  </h3>
                </div> */}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
